import { useEffect, useRef, useState } from 'react';

const STATS = [
  {
    icon: 'ri-user-heart-line',
    value: 10000,
    suffix: '+',
    label: '누적 고객',
    sub: 'Total Clients',
  },
  {
    icon: 'ri-award-line',
    value: 20,
    suffix: '년+',
    label: '시술 경력',
    sub: 'Years of Experience',
  },
  {
    icon: 'ri-emotion-happy-line',
    value: 98,
    suffix: '%',
    label: '고객 만족도',
    sub: 'Satisfaction Rate',
  },
  {
    icon: 'ri-palette-line',
    value: 10,
    suffix: '+',
    label: '시술 종류',
    sub: 'Service Types',
  },
];

function useCountUp(target: number, duration: number, started: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    let raf: number;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  return count;
}

function StatItem({ stat, started, delay }: { stat: typeof STATS[0]; started: boolean; delay: number }) {
  const count = useCountUp(stat.value, 1800, started);

  return (
    <div
      className="flex flex-col items-center text-center px-4 py-8 md:py-10"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gold/15 border border-gold/30 mb-4">
        <i className={`${stat.icon} text-gold text-2xl`}></i>
      </div>
      <div className="font-serif text-4xl md:text-5xl font-bold text-white mb-1 tabular-nums">
        {count.toLocaleString()}<span className="text-gold">{stat.suffix}</span>
      </div>
      <p className="font-serif text-sm md:text-base font-semibold text-white/90 mb-0.5">{stat.label}</p>
      <p className="font-sans text-xs text-white/40 tracking-wider">{stat.sub}</p>
    </div>
  );
}

export default function StatsCounter() {
  const ref = useRef<HTMLElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-[#1A0F14] py-12 md:py-16 px-7 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {STATS.map((stat, i) => (
            <StatItem
              key={stat.label}
              stat={stat}
              started={started}
              delay={i * 120}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
